const SAMPLE_SIZE = 24;
const MAX_LIGHTEN = 0.5;

function toHex(value: number): string {
  return Math.round(value).toString(16).padStart(2, '0');
}

/**
 * Backdrop tone taken from the cover itself. Averaging the whole image pulls
 * the result towards grey — the photos outweigh the paper — so the most common
 * quantised colour wins instead, then gets lightened into a glow.
 *
 * Returns null when the canvas is tainted, which happens whenever the cover is
 * served from another origin than the site.
 */
export function extractCoverAccent(image: HTMLImageElement): string | null {
  const canvas = document.createElement('canvas');
  canvas.width = SAMPLE_SIZE;
  canvas.height = SAMPLE_SIZE;

  const context = canvas.getContext('2d', { willReadFrequently: true });

  if (!context) {
    return null;
  }

  context.drawImage(image, 0, 0, SAMPLE_SIZE, SAMPLE_SIZE);

  let pixels: Uint8ClampedArray;

  try {
    pixels = context.getImageData(0, 0, SAMPLE_SIZE, SAMPLE_SIZE).data;
  } catch {
    return null;
  }

  const buckets = new Map<
    number,
    { count: number; r: number; g: number; b: number }
  >();

  for (let i = 0; i < pixels.length; i += 4) {
    const r = pixels[i]!;
    const g = pixels[i + 1]!;
    const b = pixels[i + 2]!;
    const key = ((r >> 4) << 8) | ((g >> 4) << 4) | (b >> 4);
    const bucket = buckets.get(key) ?? { count: 0, r: 0, g: 0, b: 0 };

    bucket.count += 1;
    bucket.r += r;
    bucket.g += g;
    bucket.b += b;
    buckets.set(key, bucket);
  }

  const dominant = [...buckets.values()].sort((a, b) => b.count - a.count)[0];

  if (!dominant) {
    return null;
  }

  const r = dominant.r / dominant.count;
  const g = dominant.g / dominant.count;
  const b = dominant.b / dominant.count;

  // Dark covers have to be lifted to read as a glow, light ones already do
  const luminance = (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255;
  const amount = MAX_LIGHTEN * (1 - luminance);
  const lighten = (channel: number) => channel + (255 - channel) * amount;

  return `#${toHex(lighten(r))}${toHex(lighten(g))}${toHex(lighten(b))}`;
}
